import { cva, type VariantProps } from '@veraclins-dev/cva';

import { styleProps } from './styles';

/** ::::::::: Typography ::::::::: */
const typographyVariants = cva({
  base: '',
  responsive: {
    ...styleProps,
  },
  variants: {
    variant: {
      // Two tiers. hero and h1 to h4 step up once at lg (1024px), where the
      // content column is wider than any phone even beside the Sidebar. h5, h6
      // and the text roles keep one size, and stat and stat-sm keep their own
      // display chain. Each `text-{size}/{n}` pair sets the size and
      // line-height together, so a caller's `text-*` override brings its own
      // default line-height. On the variants that step, the `lg:` pair sets
      // line-height too, so an unprefixed caller `text-*` or `leading-*`
      // applies below 1024px only.
      //
      // `break-words` (`overflow-wrap: break-word`) on every heading. A word
      // longer than the line is rare in English and common in German, Dutch and
      // in machine strings such as an address or an id. Without it the word
      // runs past the container: `hero` at 36px overflows a 320px screen, and
      // so does `h1` now that it starts at 30px. `break-word` only splits a
      // word that has no other way to fit, so ordinary wrapping and
      // `text-wrap-style: pretty` are untouched. `break-all`, which splits any
      // word at any character, is not what we want here.
      //
      // `[text-wrap-style:pretty]`, not Tailwind's `text-pretty`. `text-pretty`
      // sets the `text-wrap` shorthand, so it also sets `text-wrap-mode: wrap`.
      // Tailwind emits `.truncate` before `.text-pretty`, so that wrap mode beats
      // the `white-space: nowrap` in `truncate`, and a truncated heading wraps
      // instead. The longhand sets only the style and leaves the mode alone.
      hero: 'text-4xl/10.5 font-bold tracking-tight break-words [text-wrap-style:pretty] lg:text-5xl/14',
      h1: 'text-3xl/9 font-semibold tracking-tight break-words [text-wrap-style:pretty] lg:text-4xl/10.5',
      h2: 'text-2xl/7.5 font-semibold tracking-tight break-words [text-wrap-style:pretty] lg:text-3xl/9',
      h3: 'text-xl/6.5 font-semibold break-words [text-wrap-style:pretty] lg:text-2xl/7.5',
      h4: 'text-lg/6 font-semibold break-words [text-wrap-style:pretty] lg:text-xl/6.5',
      h5: 'text-base/5.5 font-semibold break-words [text-wrap-style:pretty]',
      h6: 'text-sm/5 font-semibold break-words [text-wrap-style:pretty]',
      stat: 'inline-block text-5xl font-semibold leading-none tracking-tight tabular-nums md:text-6xl vl:text-7xl',
      'stat-sm':
        'inline-block text-3xl font-semibold leading-none tracking-tight tabular-nums vl:text-4xl',
      subtitle1: 'text-base/6 font-medium',
      subtitle2: 'text-sm/5 font-medium',
      body1: 'text-base/6.5 font-normal',
      body2: 'text-sm/5.25 font-normal',
      caption: 'text-xs/4.5 font-normal',
      // 10px sits below the size at which body copy stays legible, so `small`
      // now renders as `caption` does. It stays in the union so existing
      // callers keep working; prefer `caption` in new code.
      small: 'text-xs/4.5 font-normal',
      overline: 'text-xs/4 font-medium uppercase tracking-wider',
      inherit: '',
    },
    family: {
      inherit: '',
      sans: 'font-sans',
      mono: 'font-mono',
    },
    align: {
      inherit: '',
      left: 'text-left',
      start: 'text-start',
      center: 'text-center',
      end: 'text-end',
      right: 'text-right',
      justify: 'text-justify',
    },
    gutterBottom: {
      true: 'mb-4',
      false: '',
    },
    noWrap: {
      true: 'truncate',
      false: '',
    },
    // Cuts the text to N lines with an ellipsis. `line-clamp-N` sets
    // `overflow: hidden` and `display: -webkit-box`, and `truncate` sets
    // `white-space: nowrap`, so the two cannot both apply. The component drops
    // `noWrap` when `lineClamp` is set. The four classes are also listed in
    // `public/css/styles-base.css` so the shipped CSS contains them.
    lineClamp: {
      1: 'line-clamp-1',
      2: 'line-clamp-2',
      3: 'line-clamp-3',
      4: 'line-clamp-4',
    },
  },
  defaultVariants: {
    variant: 'body2',
    family: 'inherit',
    align: 'inherit',
    gutterBottom: false,
    noWrap: false,
  },
});

type TypographyVariants = VariantProps<typeof typographyVariants>;

export { type TypographyVariants, typographyVariants };
